/*
  QR Pay screens rebuilt in HTML from the Figma prototype (QR-Pay file, flow 141237:132706),
  using the Redwood tokens the screens are built with. Each screen renders at its native
  390×844 and is scaled into the phone, so it stays crisp at any zoom.
*/
import colorStrip from './assets/color-strip.png';

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const money = (v) => `$${v.toFixed(2)}`;

/* —— Icons (paths from the Figma assets / SF Symbols look-alikes) —— */
const Overflow = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <circle cx="3.33" cy="10" r="1.67" fill="#161513" />
    <circle cx="10" cy="10" r="1.67" fill="#161513" />
    <circle cx="16.67" cy="10" r="1.67" fill="#161513" />
  </svg>
);
const Minus = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="M18.33 10.83H1.67V9.17h16.66v1.66Z" fill="#161513" /></svg>
);
const Plus = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="M9.17 9.17v-7.5h1.66v7.5h7.5v1.66h-7.5v7.5H9.17v-7.5h-7.5V9.17h7.5Z" fill="#161513" /></svg>
);
const Success = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" fill="#508223"
      d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Zm-2-5.586 7.707-7.707-1.414-1.414L10 13.586l-2.293-2.293-1.414 1.414L10 16.414Z" />
  </svg>
);
const Check = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="m3.5 8.5 3 3 6-7" fill="none" stroke="#161513" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

/* —— iOS Safari chrome —— */
const StatusBar = () => (
  <div className="qps-status">
    <span className="qps-status__time">9:41</span>
    <span className="qps-status__icons" aria-hidden="true">
      <svg width="17" height="11" viewBox="0 0 17 11"><rect x="0" y="7" width="3" height="4" rx=".7" /><rect x="4.7" y="5" width="3" height="6" rx=".7" /><rect x="9.4" y="2.5" width="3" height="8.5" rx=".7" /><rect x="14" y="0" width="3" height="11" rx=".7" /></svg>
      <svg width="15" height="11" viewBox="0 0 15 11"><path d="M7.5 2.3c2 0 3.9.8 5.3 2.1l1-1A8.9 8.9 0 0 0 7.5.9 8.9 8.9 0 0 0 1.2 3.4l1 1a7.6 7.6 0 0 1 5.3-2.1Zm0 3c1.2 0 2.3.4 3.2 1.2l1-1a6 6 0 0 0-8.4 0l1 1a4.6 4.6 0 0 1 3.2-1.2Zm0 3c.4 0 .8.2 1.1.4L7.5 9.8 6.4 8.7c.3-.2.7-.4 1.1-.4Z" /></svg>
      <svg width="25" height="12" viewBox="0 0 25 12"><rect x=".5" y=".5" width="21" height="11" rx="3" fill="none" stroke="currentColor" opacity=".4" /><rect x="2" y="2" width="18" height="8" rx="1.6" /><path d="M23 4v4c.8-.3 1.4-1.1 1.4-2S23.8 4.3 23 4Z" opacity=".45" /></svg>
    </span>
  </div>
);

const SafariHeader = () => (
  <div className="qps-safari">
    <StatusBar />
    <div className="qps-url">
      <span className="qps-url__aa">
        <small>A</small>A
      </span>
      <span className="qps-url__domain">
        <svg width="10" height="13" viewBox="0 0 10 13" aria-hidden="true"><path d="M2 5.5V4a3 3 0 1 1 6 0v1.5h.5c.6 0 1 .4 1 1V12c0 .6-.4 1-1 1h-7c-.6 0-1-.4-1-1V6.5c0-.6.4-1 1-1H2Zm1.3 0h3.4V4a1.7 1.7 0 1 0-3.4 0v1.5Z" fill="currentColor" /></svg>
        domain.com
      </span>
      <svg className="qps-url__reload" width="16" height="17" viewBox="0 0 16 17" aria-hidden="true"><path d="M8 2.5V0l3.5 3L8 6V3.8A5.3 5.3 0 1 0 13.3 9h1.3A6.6 6.6 0 1 1 8 2.5Z" fill="currentColor" /></svg>
    </div>
  </div>
);

const SafariToolbar = () => (
  <div className="qps-toolbar" aria-hidden="true">
    <svg width="22" height="22" viewBox="0 0 22 22"><path d="M14 3 6 11l8 8" fill="none" stroke="#2e7cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    <svg width="22" height="22" viewBox="0 0 22 22"><path d="m8 3 8 8-8 8" fill="none" stroke="#cecece" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    <svg width="22" height="24" viewBox="0 0 22 24"><path d="M11 2v13M6.5 6.5 11 2l4.5 4.5M7 10H5v12h12V10h-2" fill="none" stroke="#2e7cf6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    <svg width="24" height="22" viewBox="0 0 24 22"><path d="M12 5c-2-2-6-2.5-10-1.5v15c4-1 8-.5 10 1.5 2-2 6-2.5 10-1.5v-15C18 2.5 14 3 12 5Zm0 0v15" fill="none" stroke="#2e7cf6" strokeWidth="1.8" strokeLinejoin="round" /></svg>
    <svg width="22" height="22" viewBox="0 0 22 22"><rect x="2" y="6" width="14" height="14" rx="3" fill="none" stroke="#2e7cf6" strokeWidth="1.8" /><path d="M6 6V5a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-1" fill="none" stroke="#2e7cf6" strokeWidth="1.8" /></svg>
    <span className="qps-home" />
  </div>
);

/* —— Redwood page header with the generic color strip —— */
const PageHeader = ({ title, subtitle, badge }) => (
  <div className="qps-header">
    <div className="qps-header__main">
      <div className="qps-header__text">
        <div className="qps-header__title">
          {title}
          {badge ? <span className="qps-badge">{badge}</span> : null}
        </div>
        {subtitle ? <div className="qps-header__sub">{subtitle}</div> : null}
      </div>
      <span className="qps-header__menu"><Overflow /></span>
    </div>
    <div className="qps-strip" style={{ backgroundImage: `url(${colorStrip})` }} />
  </div>
);

const Receipt = () => (
  <div className="qps-receipt">
    <div className="qps-row"><span>2 James</span><span>Page 1</span></div>
    <div className="qps-dash">-----------------------------------</div>
    <div>CHK 569</div>
    <div className="qps-c">Bob’s Burger</div>
    <div className="qps-c">2/15/2023 10:07 PM</div>
    <div className="qps-dash">-----------------------------------</div>
    <div className="qps-row"><span>2 Chips &amp; Salsa</span><span>25.00</span></div>
    <div className="qps-row"><span>2 Fried Mozz Chz</span><span>25.00</span></div>
    <div className="qps-row"><span>4 Deluxe Burgers</span><span>100.00</span></div>
    <div className="qps-row"><span>4 French Fries</span><span>50.00</span></div>
    <div className="qps-row"><span>&nbsp;&nbsp;Subtotal</span><span>$200.00</span></div>
    <div>11.03 PM</div>
    <div>&nbsp;&nbsp;Total Due $200.00</div>
  </div>
);

const Button = ({ kind = 'outline', children, pressed = false, disabled = false, navy = false, compact = false, tap }) => (
  <div
    data-tap={tap}
    className={[
      'qps-btn', `qps-btn--${kind}`, pressed && 'is-pressed', disabled && 'is-disabled', navy && 'qps-btn--navy', compact && 'qps-btn--compact',
    ].filter(Boolean).join(' ')}
  >
    {children}
  </div>
);

/* —— Screens —— */
export const CheckScreen = ({ press } = {}) => (
  <div className="qps-screen">
    <SafariHeader />
    <PageHeader title="Bob’s Burger" />
    <div className="qps-body"><Receipt /></div>
    <div className="qps-footer">
      <div className="qps-footer__total">$200.00 Total</div>
      <Button tap="split" pressed={press === 'split'}>Split Check</Button>
      <Button kind="cta">Pay Full</Button>
    </div>
    <SafariToolbar />
  </div>
);

const Stepper = ({ label, value, focused, pressMinus, pressPlus, id }) => (
  <div className="qps-field">
    <div className="qps-label">{label}</div>
    <div className={`qps-stepper${focused ? ' is-focused' : ''}`}>
      <span data-tap={`${id}-`} className={`qps-stepper__btn${pressMinus ? ' is-pressed' : ''}`}><Minus /></span>
      <span className="qps-stepper__value">
        {value}
        {focused ? <i className="qps-caret" /> : null}
      </span>
      <span data-tap={`${id}+`} className={`qps-stepper__btn${pressPlus ? ' is-pressed' : ''}`}><Plus /></span>
    </div>
  </div>
);

export const SplitScreen = ({ people, payFor, focus, press }) => {
  const youPay = (200 / people) * payFor;
  return (
    <div className="qps-screen">
      <SafariHeader />
      <PageHeader title="Split Check Equally" />
      <div className="qps-body qps-body--fields">
        <Stepper id="people" label="People at the table" value={people} focused={focus === 'people'} pressMinus={press === 'people-'} pressPlus={press === 'people+'} />
        <Stepper id="payFor" label="People you pay for" value={payFor} focused={focus === 'payFor'} pressMinus={press === 'payFor-'} pressPlus={press === 'payFor+'} />
      </div>
      <div className="qps-totals">
        <div className="qps-row qps-row--md"><span>Check total</span><span>$200.00</span></div>
        <div className="qps-row qps-row--lg"><span>You Pay</span><span>{money(youPay)}</span></div>
      </div>
      <div className="qps-footer">
        <Button>Cancel</Button>
        <Button kind="cta" tap="continue" pressed={press === 'continue'}>Continue</Button>
      </div>
      <SafariToolbar />
    </div>
  );
};

const TIPS = [['18%', '$18.00'], ['20%', '$20.00'], ['25%', '$25.00']];
export const TipScreen = ({ selected, press }) => {
  const tip = selected === '20%' ? 20 : 0;
  return (
    <div className="qps-screen">
      <SafariHeader />
      <PageHeader title="Add Tip" subtitle="Paying for 2 of 4 at the table" />
      <div className="qps-body qps-body--tips">
        <div className="qps-tips">
          {TIPS.map(([pct, amt]) => (
            <div key={pct} data-tap={pct} className={`qps-tip${selected === pct ? ' is-selected' : ''}${press === pct ? ' is-pressed' : ''}`}>
              <b>{pct}</b><span>{amt}</span>
            </div>
          ))}
        </div>
        <div className="qps-tips qps-tips--2">
          <div className="qps-tip"><b>Custom Tip</b><span>$0.00</span></div>
          <div className="qps-tip qps-tip--muted"><b>No Tip</b><span>$0.00</span></div>
        </div>
      </div>
      <div className="qps-totals">
        <div className="qps-row qps-row--md"><span>Check total</span><span>$100.00</span></div>
        {tip ? <div className="qps-row qps-row--md"><span>20% Tip</span><span>$20.00</span></div> : null}
        <div className="qps-row qps-row--lg"><span>You Pay</span><span>{money(100 + tip)}</span></div>
      </div>
      <div className="qps-footer">
        <Button>Cancel</Button>
        <Button kind="cta" tap="tipContinue" disabled={!tip} pressed={press === 'continue'}>Continue</Button>
      </div>
      <SafariToolbar />
    </div>
  );
};

const CARD = '4556 5346 3463 4694';
export const PayScreen = ({ typed, press }) => {
  const shown = CARD.slice(0, Math.round(clamp(typed) * CARD.length));
  const done = typed >= 1;
  return (
    <div className="qps-screen">
      <SafariHeader />
      <PageHeader title="Make payment" subtitle="Paying for 2 of 4 at the table" />
      <div className="qps-body qps-body--pay">
        <div className="qps-card">
          <div className="qps-card__head">
            <span className="qps-card__icon"><svg width="18" height="12" viewBox="0 0 18 12" aria-hidden="true"><rect width="18" height="12" rx="2" fill="#161513" /><rect y="3" width="18" height="2.4" fill="#fff" /></svg></span>
            Cards
          </div>
          <p className="qps-card__note">All fields are required unless marked otherwise.</p>
          <label className="qps-card__label">Card number</label>
          <div className="qps-input is-active" data-tap="card">
            <span>{shown}{!done ? <i className="qps-caret" /> : null}</span>
            <span className="qps-visa">VISA</span>
          </div>
          <div className="qps-card__row">
            <div>
              <label className="qps-card__label">Expiry date</label>
              <div className="qps-input"><span>11/27</span><Check /></div>
              <p className="qps-card__help">Front of card in MM/YY format</p>
            </div>
            <div>
              <label className="qps-card__label">Security code</label>
              <div className="qps-input"><span>430</span><Check /></div>
              <p className="qps-card__help">3 digits on back of card</p>
            </div>
          </div>
          <label className="qps-card__label">Name on card</label>
          <div className="qps-input"><span>Mouric Shambu</span><Check /></div>
        </div>
        <Button kind="cta" navy compact tap="pay" pressed={press === 'pay'}>Pay $120.00</Button>
        <Button compact>Cancel</Button>
      </div>
      <SafariToolbar />
    </div>
  );
};

export const ApprovedScreen = ({ draw }) => (
  <div className="qps-screen qps-screen--approved">
    <SafariHeader />
    <div className="qps-approved">
      <svg className="qps-approved__mark" width="96" height="96" viewBox="0 0 96 96" aria-hidden="true">
        <circle cx="48" cy="48" r="42" fill="none" stroke="#508223" strokeWidth="6" pathLength="1"
          strokeDasharray="1" strokeDashoffset={1 - clamp(draw * 1.6)} transform="rotate(-90 48 48)" strokeLinecap="round" />
        <path d="m30 49 12 12 24-26" fill="none" stroke="#508223" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
          pathLength="1" strokeDasharray="1" strokeDashoffset={1 - clamp((draw - .45) * 2.2)} />
      </svg>
      <div className="qps-approved__title" style={{ opacity: clamp((draw - .5) * 3) }}>APPROVED</div>
      <div className="qps-approved__sub" style={{ opacity: clamp((draw - .6) * 3) }}>Thank you!</div>
    </div>
    <SafariToolbar />
  </div>
);

export const PaidScreen = () => (
  <div className="qps-screen">
    <SafariHeader />
    <PageHeader title="Bob’s Burger" badge="Partly Paid" subtitle="2 of 4 paid" />
    <div className="qps-body"><Receipt /></div>
    <div className="qps-footer">
      <div className="qps-paid">
        <Success />
        <div className="qps-paid__title">You paid $120.00</div>
        <div className="qps-paid__sub">$100.00 + $20.00 tip</div>
      </div>
      <Button>Email Receipt</Button>
    </div>
    <SafariToolbar />
  </div>
);

/* Persistent Safari chrome, drawn once over the moving pages (browser bars never move with the page) */
export const SafariChrome = ({ progress = null }) => (
  <div className="qps-screen qps-screen--chrome">
    <SafariHeader />
    {progress != null ? <div className="qps-load" style={{ width: `calc((100% - 20px) * ${progress})`, opacity: progress >= 1 ? 0 : 1 }} /> : null}
    <div style={{ flex: 1 }} />
    <SafariToolbar />
  </div>
);

/* —— Intro: iOS Camera scanning the QR code on a paper check —— */
const easeIO = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const QR = (() => {
  // 25×25 modules: three finder patterns + seeded data, so the code looks real and never changes
  const n = 25; let seed = 7; const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  const cells = [];
  const finder = (x, y) => x >= 0 && x < 7 && y >= 0 && y < 7;
  const inFinder = (x, y) => finder(x, y) || finder(x - (n - 7), y) || finder(x, y - (n - 7));
  for (let y = 0; y < n; y += 1) for (let x = 0; x < n; x += 1) {
    if (inFinder(x, y) || (x === 7 && y < 8) || (y === 7 && x < 8) || (x === n - 8 && y < 8) || (y === 7 && x > n - 9) || (x === 7 && y > n - 9) || (y === n - 8 && x < 8)) continue;
    if (rnd() > 0.52) cells.push([x, y]);
  }
  const eye = (x, y) => `M${x} ${y}h7v7h-7zM${x + 1} ${y + 1}v5h5v-5zM${x + 2} ${y + 2}h3v3h-3z`;
  return { n, d: cells.map(([x, y]) => `M${x} ${y}h1v1h-1z`).join('') + eye(0, 0) + eye(n - 7, 0) + eye(0, n - 7) };
})();

export const CameraScreen = ({ t = 0, measure = false }) => {
  const lock = measure ? 1 : easeIO(clamp((t - 1.5) / 0.4));
  const pill = measure ? 1 : clamp((t - 1.95) / 0.35);
  const pillPressed = t >= 2.4 && t < 2.6;
  const settle = 1 - lock * 0.85; // handheld sway calms once the code is found
  const zoom = 1 + 0.1 * easeIO(clamp((t + 0.6) / 2.2));
  const sx = Math.sin(t * 1.3) * 7 * settle; const sy = Math.sin(t * 1.75 + 1) * 5 * settle; const rot = -5 + Math.sin(t * 0.9) * 1.2 * settle;
  // Brackets: loose frame → snap to the QR code
  const loose = 46; const pad = lerpN(loose, 8, lock);
  const ps = 1 - (1 - Math.min(1, pill)) ** 3;
  return (
    <div className="qps-screen qps-cam">
      <div className="qps-cam__top"><span className="qps-status__time" style={{ color: '#fff' }}>9:41</span><span className="qps-cam__flash" /></div>
      <div className="qps-cam__view">
        <div className="qps-cam__scene" style={{ transform: `translate(${sx}px, ${sy}px) scale(${zoom}) rotate(${rot}deg)` }}>
          <div className="qps-paper">
            <div className="qps-paper__title">BOB’S BURGER</div>
            <div className="qps-paper__meta">2/15/2023 · Table 12 · Check 569</div>
            {[78, 62, 70, 54, 66].map((w, i) => <div key={i} className="qps-paper__line"><i style={{ width: `${w}%` }} /><i /></div>)}
            <div className="qps-paper__total"><span>TOTAL</span><span>$200.00</span></div>
            <div className="qps-paper__qr">
              <svg viewBox={`0 0 ${QR.n} ${QR.n}`} shapeRendering="crispEdges" aria-hidden="true"><path d={QR.d} fill="#161513" fillRule="evenodd" /></svg>
              <div className="qps-brackets" style={{ inset: -pad, opacity: measure ? 1 : clamp((t - 0.9) / 0.4), borderColor: lock > 0.5 ? '#FFD60A' : 'rgba(255,255,255,.9)' }}>
                <i /><i /><i /><i />
              </div>
            </div>
            <div className="qps-paper__scan">Scan to view and pay</div>
          </div>
        </div>
        <div
          className="qps-cam__pill"
          data-tap="qrlink"
          style={{ opacity: Math.min(1, pill * 1.6), transform: `translate(-50%, 0) scale(${(0.6 + 0.4 * ps) * (pillPressed ? 0.94 : 1)})` }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M7 1.2a5.8 5.8 0 1 0 0 11.6A5.8 5.8 0 0 0 7 1.2Zm0 0c1.6 1.7 2.4 3.6 2.4 5.8S8.6 11.1 7 12.8M7 1.2C5.4 2.9 4.6 4.8 4.6 7s.8 4.1 2.4 5.8M1.4 7h11.2" stroke="#161513" strokeWidth="1.1" fill="none" /></svg>
          domain.com
        </div>
      </div>
      <div className="qps-cam__bottom">
        <div className="qps-cam__modes"><span>VIDEO</span><b>PHOTO</b><span>PORTRAIT</span></div>
        <div className="qps-cam__controls"><span className="qps-cam__thumb" /><span className="qps-cam__shutter" /><span className="qps-cam__flip" /></div>
      </div>
    </div>
  );
};
function lerpN(a, b, x) { return a + (b - a) * x; }

/* Page loading: Safari is fetching the check (skeleton under a filling progress bar) */
export const LoadingScreen = () => (
  <div className="qps-screen">
    <SafariHeader />
    <div className="qps-skel">
      <div className="qps-skel__head"><i style={{ width: '46%' }} /><i style={{ width: '22%', height: 10 }} /></div>
      <div className="qps-skel__body">
        {[38, 60, 52, 70, 64, 58, 72, 44].map((w, i) => <i key={i} style={{ width: `${w}%` }} />)}
      </div>
    </div>
    <SafariToolbar />
  </div>
);
